import React, { useEffect, useRef, useState } from "react";
import { Text, Button, Paper, Title, Badge } from "@mantine/core";
import { useToasts } from "../../../../providers/notifications/ToastsContext";
import { useWhatsAppEmbeddedSignupWebhookApiService } from "../../services/api/WhatsAppEmbeddedSignupWebhookApiService";
import {
  useWhatsAppEmbeddedSignupJobStatusApiService,
  SIGNUP_TIMEOUT_DURATION,
  isWithinSignupTimeout,
} from "../../services/api/WhatsAppEmbeddedSignupJobStatusApiService";
import { HttpClientError } from "../../../../providers/HttpClient";
import { useEnabledFeatureFlag } from "../../../../common/feature-flagging/FeatureFlagProvider";
import FeatureFlags from "../../../../common/feature-flagging/FeatureFlags";

declare global {
  interface Window {
    fbAsyncInit: () => void;
    FB: {
      init: (params: {
        appId: string;
        autoLogAppEvents: boolean;
        xfbml: boolean;
        version: string;
      }) => void;
      login: (
        callback: (response: any) => void,
        options: {
          config_id: string;
          response_type: string;
          override_default_response_type: boolean;
          extras: {
            setup: Record<string, never>;
            featureType: string;
            sessionInfoVersion: string;
          };
        }
      ) => void;
    };
  }
}

enum WhatsAppSignupStatus {
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
  TIMEOUT = "timeout",
}

const WhatsAppEmbeddedSignup: React.FC = () => {
  // All hooks must be called at the top level, before any early returns
  const scriptLoadedRef = useRef(false);
  const isWhatsAppEmbeddedSignupEnabled = useEnabledFeatureFlag(
    FeatureFlags.WHATSAPP_EMBEDDED_SIGNUP
  );

  // State management for signup data
  const [signupData, setSignupData] = useState<{
    code?: string;
    waba_id?: string;
    phone_number_id?: string;
    business_id?: string;
  }>({});

  // State to track if we're waiting for either OAuth response or message event
  const [waitingForOAuthResponse, setWaitingForOAuthResponse] = useState(false);

  // State to track signup attempt timestamp for latency compensation
  const [lastSignupAttemptTime, setLastSignupAttemptTime] = useState<
    number | null
  >(null);

  // Authentication and notifications
  const { showToast } = useToasts();

  // API hooks
  const webhookMutation = useWhatsAppEmbeddedSignupWebhookApiService();
  const {
    data: jobStatus,
    isLoading: jobStatusLoading,
    error: jobStatusError,
  } = useWhatsAppEmbeddedSignupJobStatusApiService(
    lastSignupAttemptTime,
    isWhatsAppEmbeddedSignupEnabled
  );

  // Clear the signup attempt time when we get a terminal job status
  useEffect(() => {
    const status = (jobStatus as { status?: string })?.status;
    if (
      status === WhatsAppSignupStatus.COMPLETED ||
      status === WhatsAppSignupStatus.FAILED ||
      status === WhatsAppSignupStatus.TIMEOUT
    ) {
      // We got a terminal job status, clear the polling
      setLastSignupAttemptTime(null);
    }
  }, [jobStatus]);

  // Clear the signup attempt time after timeout expires
  useEffect(() => {
    if (!lastSignupAttemptTime) return undefined;

    const timeRemaining =
      SIGNUP_TIMEOUT_DURATION - (Date.now() - lastSignupAttemptTime);

    if (timeRemaining <= 0) {
      setLastSignupAttemptTime(null);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setLastSignupAttemptTime(null);
    }, timeRemaining);

    return () => clearTimeout(timeoutId);
  }, [lastSignupAttemptTime]);

  // Configuration values from environment
  const APP_ID = process.env.REACT_APP_WHATSAPP_APP_ID ?? "";
  const GRAPH_API_VERSION =
    process.env.REACT_APP_WHATSAPP_GRAPH_API_VERSION ?? "v18.0";
  const CONFIGURATION_ID =
    process.env.REACT_APP_WHATSAPP_CONFIGURATION_ID ?? "";
  const FEATURE_TYPE = process.env.REACT_APP_WHATSAPP_FEATURE_TYPE ?? "";

  // Function to attempt webhook call when we have all required data
  // Only makes the API call when all three required fields are present
  const attemptWebhookCall = React.useCallback(
    (data: typeof signupData) => {
      // Ensure we have all required data before making the webhook call
      if (data.code && data.waba_id && data.phone_number_id) {
        const webhookRequest = {
          code: data.code,
          waba_id: data.waba_id,
          phone_number_id: data.phone_number_id,
        };

        webhookMutation.mutate(webhookRequest, {
          onSuccess: () => {
            showToast({
              strapiId: "whatsapp_signup_initiated",
              data: { message: "WhatsApp connection initiated successfully" },
            });
            setWaitingForOAuthResponse(false);
          },
          onError: (error) => {
            // Handle different error types from HTTP 400 response
            const errorData = error.response?.data;
            if (errorData) {
              const errorMessage = errorData.error || "Connection failed";
              showToast({
                strapiId: "whatsapp_signup_error",
                data: { message: errorMessage },
              });
            } else {
              showToast({
                strapiId: "whatsapp_signup_error",
                data: {
                  message: "Failed to connect WhatsApp Business Account",
                },
              });
            }
            setWaitingForOAuthResponse(false);
          },
        });
      }
      // If data is incomplete, wait for more data to arrive via other events
    },
    [webhookMutation, showToast]
  );

  useEffect(() => {
    // Prevent duplicate script loading - important for component re-renders
    if (scriptLoadedRef.current) {
      return undefined;
    }

    // Load Facebook SDK dynamically
    // Note: SRI (Subresource Integrity) not used as Facebook updates SDK frequently
    // and their CDN is trusted. CSP and HTTPS provide adequate protection.
    const loadFacebookSDK = (): void => {
      const script = document.createElement("script");
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";

      document.body.appendChild(script);
      scriptLoadedRef.current = true;
    };

    // Initialize Facebook SDK - called automatically when SDK loads
    window.fbAsyncInit = function fbAsyncInitCallback(): void {
      window.FB.init({
        appId: APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: GRAPH_API_VERSION,
      });
    };

    // Main message event listener - handles WhatsApp signup completion data
    // This receives the WABA ID and phone number ID after successful signup
    const handleMessage = (event: MessageEvent): void => {
      // Security check: only accept messages from Facebook domains
      if (!event.origin.endsWith("facebook.com")) {
        setWaitingForOAuthResponse(false);
        return;
      }

      let data: any;

      try {
        // Try to parse as JSON first (success case)
        data = JSON.parse(event.data);
      } catch (error) {
        // If JSON parsing fails, it might be a URL-encoded error response
        if (typeof event.data !== "string") {
          setWaitingForOAuthResponse(false);
          return;
        }

        // Parse the query string format: "key1=value1&key2=value2&..."
        const urlParams = new URLSearchParams(event.data);

        // Check if this is an error response
        if (urlParams.has("error")) {
          const errorDescription = urlParams.get("error_description");
          const errorReason = urlParams.get("error_reason");

          // Show user-friendly error message
          let errorMessage = "WhatsApp signup failed";
          if (errorDescription) {
            errorMessage += `: ${decodeURIComponent(errorDescription)}`;
          } else if (errorReason) {
            errorMessage += `: ${decodeURIComponent(errorReason)}`;
          }

          showToast({
            strapiId: "whatsapp_signup_error",
            data: { message: errorMessage },
          });

          // Clear waiting state
          setWaitingForOAuthResponse(false);
          return;
        }

        // If it's not a recognizable error format, return silently
        setWaitingForOAuthResponse(false);
        return;
      }

      // Check if this is a WhatsApp Embedded Signup completion message
      if (data.type === "WA_EMBEDDED_SIGNUP") {
        // Handle both nested (data.data) and flat (data) structures
        // Facebook may send data in either format
        const whatsappData = data.data ?? data;

        // Extract the required fields from the signup completion data
        const wabaId = whatsappData.waba_id;
        const phoneNumberId = whatsappData.phone_number_id;
        const businessId = whatsappData.business_id;

        // Only proceed if we have the minimum required data
        if (wabaId && phoneNumberId) {
          // Update signup data and attempt webhook call
          setSignupData((prevData) => {
            const updatedData = {
              ...prevData,
              waba_id: wabaId,
              phone_number_id: phoneNumberId,
              business_id: businessId,
            };
            // Attempt webhook call with updated data
            attemptWebhookCall(updatedData);
            return updatedData;
          });
        }
      }
    };

    // Add event listener for WhatsApp signup completion messages
    window.addEventListener("message", handleMessage);

    loadFacebookSDK();

    // Cleanup function - removes event listener when component unmounts
    // CRITICAL: Empty dependency array ensures listener persists through OAuth flow
    return (): void => {
      window.removeEventListener("message", handleMessage);
    };
  }, []); // Remove attemptWebhookCall dependency to prevent premature cleanup

  // Handle webhook errors
  useEffect(() => {
    if (webhookMutation.isError) {
      showToast({
        strapiId: "whatsapp_webhook_error",
        data: { message: HttpClientError(webhookMutation.error) },
      });
    }
  }, [webhookMutation.isError, webhookMutation.error, showToast]);

  // Handle job status changes
  useEffect(() => {
    const status = (jobStatus as { status?: string })?.status;
    const statusReason = (jobStatus as { status_reason?: string })
      ?.status_reason;

    if (status === WhatsAppSignupStatus.FAILED) {
      showToast({
        strapiId: "whatsapp_signup_failed",
        data: {
          message: statusReason || "Connection failed",
        },
      });
    }
  }, [jobStatus, showToast]);

  // Early return if feature flag is disabled - AFTER all hooks
  if (!isWhatsAppEmbeddedSignupEnabled) {
    return null;
  }

  // Facebook OAuth callback - receives the authorization code
  // This is the first part of the two-step signup process
  const fbLoginCallback = (response: any): void => {
    if (response.authResponse) {
      const { code } = response.authResponse;

      // Set waiting state to show processing UI
      setWaitingForOAuthResponse(true);

      // Store the OAuth code and attempt webhook call
      // This may not complete if we don't have WABA data yet
      setSignupData((prevData) => {
        const updatedData = { ...prevData, code };
        // Attempt webhook call with updated data
        attemptWebhookCall(updatedData);
        return updatedData;
      });

      // Show processing message if we're still waiting for WhatsApp data
      // The message event with WABA/phone data may come after OAuth
      if (!signupData.waba_id || !signupData.phone_number_id) {
        showToast({
          strapiId: "whatsapp_signup_processing",
          data: {
            message: "Processing WhatsApp connection. Please wait...",
          },
        });
      }
    } else if (response.status === "unknown") {
      // User cancelled the OAuth flow
      showToast({
        strapiId: "whatsapp_signup_cancelled",
        data: { message: "WhatsApp connection was cancelled" },
      });
    } else {
      // OAuth failed for other reasons
      showToast({
        strapiId: "whatsapp_signup_error",
        data: { message: "Failed to connect to Facebook. Please try again." },
      });
    }
  };

  // Launch WhatsApp signup flow - initiates the OAuth process
  const launchWhatsAppSignup = (): void => {
    // Ensure Facebook SDK is loaded before attempting login
    if (!window.FB) {
      showToast({
        strapiId: "facebook_sdk_not_loaded",
        data: { message: "Facebook SDK not loaded yet. Please try again." },
      });
      return;
    }

    // Track the signup attempt time for latency compensation
    setLastSignupAttemptTime(Date.now());

    setWaitingForOAuthResponse(true);

    window.FB.login(fbLoginCallback, {
      config_id: CONFIGURATION_ID,
      response_type: "code",
      override_default_response_type: true,
      extras: {
        setup: {},
        featureType: FEATURE_TYPE,
        sessionInfoVersion: "3",
      },
    });
  };

  // Connection status logic
  // Type guard to ensure jobStatus has a 'status' property
  type JobStatusWithStatus = { status: string; status_reason?: string };
  const jobStatusWithStatus = jobStatus as JobStatusWithStatus | null;

  // Helper functions for status checks
  const isStatusCompleted = (): boolean =>
    jobStatusWithStatus?.status === WhatsAppSignupStatus.COMPLETED;

  const isStatusProcessing = (): boolean =>
    jobStatusWithStatus?.status === WhatsAppSignupStatus.PROCESSING;

  const isStatusTimeout = (): boolean =>
    jobStatusWithStatus?.status === WhatsAppSignupStatus.TIMEOUT;

  const isStatusFailed = (): boolean =>
    jobStatusWithStatus?.status === WhatsAppSignupStatus.FAILED;

  const is204StatusFound = (): boolean =>
    jobStatusError?.response?.status === 204;

  const isWithinSignupTimeoutPeriod = (): boolean =>
    isWithinSignupTimeout(lastSignupAttemptTime);

  const shouldTreat204AsLoading = (): boolean =>
    is204StatusFound() && isWithinSignupTimeoutPeriod();

  // Helper function to extract phone number from job status
  const getWhatsAppPhoneNumber = (): string => {
    const statusMessage =
      jobStatus?.onboarding_progress?.phases?.[0]?.steps?.[0]?.status_message;
    if (!statusMessage) {
      return "0XX XXXXXXX";
    }

    // Extract phone number using a more robust pattern that matches international phone numbers
    const phoneRegex = /\+\d{1,4}\s?\d{1,4}\s?\d{3,4}\s?\d{3,4}/;
    const phoneMatch = phoneRegex.exec(statusMessage);

    return phoneMatch ? phoneMatch[0].trim() : "0XX XXXXXXX";
  };

  const canRetrySignup = (): boolean => isStatusTimeout() || isStatusFailed();

  const isCurrentlyConnecting = (): boolean =>
    isStatusProcessing() || waitingForOAuthResponse;

  // Derived status values using helper functions
  const isConnected = isStatusCompleted();
  const isConnecting = isCurrentlyConnecting();

  const shouldEnableSignupButton = (): boolean =>
    !waitingForOAuthResponse &&
    !shouldTreat204AsLoading() &&
    (is204StatusFound() ||
      canRetrySignup() ||
      (jobStatus !== null && !jobStatusLoading && !isConnecting));

  // Status pill configuration
  const STATUS_PILL_STYLES = {
    loading: {
      color: "gray",
      text: "Loading...",
      textColor: "#6B7280",
      backgroundColor: "#F3F4F6",
    },
    connected: {
      color: "green",
      text: "Connected",
      textColor: "#065F46",
      backgroundColor: "rgba(34, 197, 94, 0.3)",
    },
    failed: {
      color: "red",
      text: "Failed",
      textColor: "#991B1B",
      backgroundColor: "rgba(239, 68, 68, 0.3)",
    },
    connecting: {
      color: "yellow",
      text: "In progress",
      textColor: "#92400E",
      backgroundColor: "rgba(234, 179, 8, 0.3)",
    },
  } as const;

  const PILL_TEXT_STYLE = {
    fontSize: "14px",
    lineHeight: "20px",
    minHeight: "32px",
    display: "inline-flex",
    alignItems: "center",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "8px",
    paddingRight: "8px",
  } as const;

  type StatusPillReturnType = {
    color: string;
    text: string;
    backgroundColor: string;
    textColor: string;
  } | null;

  const getStatusPill = (): StatusPillReturnType => {
    if (jobStatusLoading || shouldTreat204AsLoading()) {
      return STATUS_PILL_STYLES.loading;
    }

    if (isConnected) {
      return STATUS_PILL_STYLES.connected;
    }

    if (isConnecting) {
      return STATUS_PILL_STYLES.connecting;
    }

    if (isStatusFailed()) {
      return STATUS_PILL_STYLES.failed;
    }

    // Default state (not started)
    return null;
  };

  const renderCardStatusPill = (): JSX.Element | null => {
    const statusPillConfig = getStatusPill();

    // If no status pill config, return null (no pill to render)
    return (
      statusPillConfig && (
        <div style={{ marginBottom: "16px" }}>
          <Badge
            size="lg"
            radius="xl"
            variant="filled"
            style={{
              fontWeight: 500,
              textTransform: "none",
              backgroundColor: statusPillConfig.backgroundColor,
              border: "none",
            }}
          >
            <span
              style={{
                color: statusPillConfig.textColor,
                ...PILL_TEXT_STYLE,
              }}
            >
              {statusPillConfig.text}
            </span>
          </Badge>
        </div>
      )
    );
  };

  const renderConnectionDescription = (): JSX.Element =>
    isConnected ? (
      <div className="px-1">
        <span
          style={{ fontSize: "12px", lineHeight: "20px", color: "#9CA3AF" }}
        >
          Your WhatsApp Number
        </span>
        <Text
          mb={32}
          style={{ fontSize: "14px", lineHeight: "20px", color: "#333" }}
        >
          {getWhatsAppPhoneNumber()}
        </Text>
      </div>
    ) : (
      <Text
        mb={32}
        style={{ fontSize: "14px", lineHeight: "20px", color: "#9CA3AF" }}
      >
        Create or connect your company&apos;s existing WhatsApp Business account
        to set up your white label Jem account.
      </Text>
    );

  const renderCardTitle = (): JSX.Element => (
    <Title
      order={3}
      mb={16}
      style={{ fontSize: "18px", fontWeight: 600, color: "#333" }}
    >
      {isConnected
        ? "Your WhatsApp Business Account"
        : "Link your WhatsApp Business Account"}
    </Title>
  );

  // Render the main component
  return (
    <div style={{ marginTop: "32px" }}>
      <Paper
        p={24}
        withBorder
        radius="md"
        style={{
          width: "390px",
          borderColor: "#829099",
          backgroundColor: "#ffffff",
        }}
      >
        {renderCardStatusPill()}
        {renderCardTitle()}
        {renderConnectionDescription()}

        <Button
          color="dark"
          variant="outline"
          onClick={launchWhatsAppSignup}
          disabled={!shouldEnableSignupButton()}
          loading={
            jobStatusLoading ||
            waitingForOAuthResponse ||
            shouldTreat204AsLoading()
          }
          style={{
            height: "40px",
            minWidth: "200px",
            fontWeight: "bold",
            borderColor: "black",
          }}
          styles={(theme) => ({
            outline: {
              "&:disabled, &[data-disabled]": {
                borderColor: `${theme.colors.gray[4]} !important`,
                color: `${theme.colors.gray[6]} !important`,
                backgroundColor: "white !important",
                cursor: "not-allowed",
                opacity: 0.8,
              },
            },
          })}
        >
          Continue With Facebook
        </Button>
      </Paper>
    </div>
  );
};

export default WhatsAppEmbeddedSignup;
